// Worker の集まり。空いている Worker に1ジョブずつ割り当てる（並び順は呼び出し側で保つ）
// Worker からのメッセージ: ready / init_error / progress{id} / done{id} / error{id, message, log}

const toError = (e, fallback) => (e instanceof Error ? e : new Error((e && e.message) || fallback));

const cancelledError = () => Object.assign(new Error('cancelled'), { code: 'cancelled' });

// 読み込みに失敗した Worker は error を出さずに黙ることがあるため、準備が揃うまでの待ち時間に上限を置く
export const START_TIMEOUT_MS = 30000;

/**
 * 全 Worker の準備（ready）ができたら resolve する。起動に失敗したら作った Worker をすべて止めて reject
 * ジョブの失敗（error）はそのジョブだけ、Worker が落ちたらそのジョブを失敗させて Worker を作り直す
 * @param {{ size: number, createWorker: () => Worker, initMessage: object, startTimeoutMs?: number }} options
 * @returns {Promise<{ run: (message: object, options?: { transfer?: Transferable[], owner?: any, onStart?: Function, onProgress?: Function, priority?: boolean }) => Promise<object>, cancel: (owner: any) => void, dispose: () => void }>}
 */
export const createWorkerPool = ({ size, createWorker, initMessage, startTimeoutMs = START_TIMEOUT_MS }) => new Promise((resolveStart, rejectStart) => {
    const slots = new Set();
    const waiting = [];
    let failure = null;
    let started = false;
    let nextId = 0;
    let startTimer = null;

    const fail = (error) => {
        if (failure) return;
        failure = error;
        clearTimeout(startTimer);
        slots.forEach((slot) => {
            slot.worker.terminate();
            if (slot.job) slot.job.reject(error);
        });
        slots.clear();
        waiting.splice(0).forEach((job) => job.reject(error));
        rejectStart(error);
    };

    const dispatch = () => {
        if (failure) return;
        slots.forEach((slot) => {
            if (!slot.ready || slot.job || !waiting.length) return;
            const job = waiting.shift();
            slot.job = job;
            slot.worker.postMessage({ ...job.message, id: job.id }, job.transfer || []);
            if (job.onStart) job.onStart();
        });
    };

    // 落ちた・取り消した Worker の代わりを用意する
    const replace = (slot) => {
        slot.worker.terminate();
        slots.delete(slot);
        try {
            spawn();
        } catch (e) {
            fail(toError(e, 'worker error'));
        }
    };

    const spawn = () => {
        const slot = { worker: createWorker(), ready: false, job: null };
        slots.add(slot);
        slot.worker.onmessage = ({ data }) => onMessage(slot, data);
        slot.worker.onerror = (e) => {
            // 外側（画面）へ伝わらないようにする。失敗の扱いはここで決める
            if (e && e.preventDefault) e.preventDefault();
            const error = toError(e, 'worker error');
            if (!started || !slot.ready) {
                fail(error);
                return;
            }
            const { job } = slot;
            slot.job = null;
            if (job) job.reject(error);
            replace(slot);
        };
        slot.worker.postMessage(initMessage);
    };

    const onMessage = (slot, data) => {
        if (failure || !slots.has(slot)) return;
        if (data.type === 'ready') {
            slot.ready = true;
            if (!started && [...slots].every((s) => s.ready)) {
                started = true;
                clearTimeout(startTimer);
                resolveStart({ run, cancel, dispose });
            }
            dispatch();
            return;
        }
        if (data.type === 'init_error') {
            fail(new Error(data.message));
            return;
        }
        const { job } = slot;
        if (!job || job.id !== data.id) return;
        if (data.type === 'progress') {
            if (job.onProgress) job.onProgress(data);
            return;
        }
        slot.job = null;
        if (data.type === 'error') job.reject(Object.assign(new Error(data.message), { log: data.log }));
        else job.resolve(data);
        dispatch();
    };

    // priority のジョブは待ち行列の先頭に入れる
    const run = (message, { transfer, owner, onStart, onProgress, priority = false } = {}) => {
        if (failure) return Promise.reject(failure);
        return new Promise((resolve, reject) => {
            const job = { id: nextId++, message, transfer, owner, onStart, onProgress, resolve, reject };
            if (priority) waiting.unshift(job);
            else waiting.push(job);
            dispatch();
        });
    };

    const cancel = (owner) => {
        if (failure) return;
        for (let i = waiting.length - 1; i >= 0; i--) {
            if (waiting[i].owner === owner) waiting.splice(i, 1)[0].reject(cancelledError());
        }
        // ffmpeg の実行は途中で止められないので、Worker ごと止めて作り直す
        [...slots].forEach((slot) => {
            if (!slot.job || slot.job.owner !== owner) return;
            const { job } = slot;
            slot.job = null;
            job.reject(cancelledError());
            replace(slot);
        });
    };

    const dispose = () => fail(Object.assign(new Error('worker pool disposed'), { code: 'cancelled' }));

    startTimer = setTimeout(() => fail(new Error('workers did not start')), startTimeoutMs);
    try {
        for (let i = 0; i < size; i++) spawn();
    } catch (e) {
        fail(toError(e, 'worker error'));
    }
});
